// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { useShopStore } from '../../store/shopStore';
import { shopsAPI } from '../../services/api';
import { navigationConfig, NavCategory } from '../../config/navigationConfig';
import { hasPermission, RoleType, getRoleInfo, Permissions } from '../../config/rolesConfig';
import {
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

// Full class strings (so Tailwind's content scan keeps them) per role.
const ROLE_BADGE: Record<string, string> = {
  superuser: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  admin: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  florist: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  driver: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  viewer: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { isDarkMode, toggleTheme } = useThemeStore();
  const { shops, activeShopId, setShops, setActiveShop } = useShopStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userRole = (user?.role || 'viewer') as RoleType;
  const canPickShop =
    hasPermission(userRole, Permissions.ORDERS_READ) || hasPermission(userRole, Permissions.DISPATCH_READ);

  // Load the shop list once for the selector (non-driver roles).
  useEffect(() => {
    if (!canPickShop) return;
    shopsAPI
      .list()
      .then((r) => setShops(r.data.shops || []))
      .catch(() => {});
  }, [canPickShop, setShops]);

  const onShopChange = (id: string) => {
    setActiveShop(id);
    // Reload so every screen refetches under the newly selected shop.
    window.location.reload();
  };

  const filteredNavigation = useMemo((): NavCategory[] => {
    const userRole = (user?.role || 'viewer') as RoleType;
    return navigationConfig
      .map((category) => ({
        ...category,
        items: category.items.filter((item) => !item.permission || hasPermission(userRole, item.permission)),
      }))
      .filter((category) => category.items.length > 0);
  }, [user?.role]);

  const roleInfo = useMemo(() => getRoleInfo((user?.role || 'viewer') as RoleType), [user?.role]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-100'}`}>
      {sidebarOpen && (
        <div className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 transform bg-white transition-transform duration-200 ease-in-out dark:bg-gray-800 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between border-b bg-rose-600 px-4 dark:border-gray-700">
            <Link to="/dashboard" className="flex items-center gap-2 text-xl font-bold text-white">
              <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2c1.5 2 4 2.5 4 5a4 4 0 11-8 0c0-2.5 2.5-3 4-5z" opacity=".9" />
                <path d="M12 11c0 4 3 5 6 5-1 3-4 4-6 4s-5-1-6-4c3 0 6-1 6-5z" />
              </svg>
              {t('app.name')}
            </Link>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4">
            {filteredNavigation.map((category) => (
              <div key={category.nameKey} className="mb-4">
                <h3 className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {t(category.nameKey)}
                </h3>
                <div className="mt-2 space-y-1">
                  {category.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center px-4 py-2 text-sm font-medium ${
                          isActive
                            ? 'border-r-2 border-rose-600 bg-rose-50 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300'
                            : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                      >
                        <item.icon className="mr-3 h-5 w-5" />
                        {t(item.nameKey)}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
            <p className="text-center text-xs font-light text-gray-400 dark:text-gray-500">
              Copyright 2026, Auxilio Technologies LLC
            </p>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-10 border-b bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex h-16 items-center justify-between px-4">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Bars3Icon className="h-6 w-6 text-gray-500" />
            </button>
            <div className="flex flex-1 items-center justify-end gap-4">
              {canPickShop && shops.length > 0 && (
                <select
                  value={activeShopId || ''}
                  onChange={(e) => onShopChange(e.target.value)}
                  title={t('shop.label')}
                  className="max-w-[200px] rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {shops.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
              <button onClick={toggleTheme} className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                {isDarkMode ? <SunIcon className="h-5 w-5 text-gray-400" /> : <MoonIcon className="h-5 w-5 text-gray-600" />}
              </button>
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-end">
                  <span className="text-sm text-gray-600 dark:text-gray-300">{user?.email}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${ROLE_BADGE[roleInfo.id] || ROLE_BADGE.viewer}`}>
                    {t(roleInfo.nameKey)}
                  </span>
                </div>
                <button
                  onClick={logout}
                  title={t('auth.logout')}
                  className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">{children}</main>
      </div>
    </div>
  );
};

export default MainLayout;
